import { useNavigate } from "react-router-dom";
import { useUserStore } from "../../store/userStore";
import { useEffect, useState } from "react";
import Navbar from "../../components/tickets/navBar";
import { getMyOrders, getMyTickets } from "../../api/ticketsPlatform/auth";
import Footer from "../../components/tickets/footer";
import { DownloadIcon } from "@heroicons/react/outline";

export default function UserDashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, token, clearAuth } = useUserStore();

  const [activeTab, setActiveTab] = useState("orders"); // "orders" | "tickets"

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);

  // Redirect if not logged in
  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate("/tickets-store/auth");
    }
  }, [isAuthenticated, user, navigate]);

  // Fetch orders
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const fetch = async () => {
      try {
        const data = await getMyOrders(token);
        setOrders(data?.results || data || []);
      } catch (err) {
        console.error("Orders fetch error:", err);
      } finally {
        setOrdersLoading(false);
      }
    };
    fetch();
  }, [isAuthenticated, token]);

  // Fetch tickets
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const fetch = async () => {
      try {
        const data = await getMyTickets(token);
        setTickets(data?.results || data || []);
      } catch (err) {
        console.error("Tickets fetch error:", err);
      } finally {
        setTicketsLoading(false);
      }
    };
    fetch();
  }, [isAuthenticated, token]);

  if (!isAuthenticated || !user) return null;

  const handleLogout = () => {
    clearAuth();
    navigate("/tickets-store/");
  };

  return (
    <div className="bg-[#021610] text-white min-h-screen flex flex-col">
      <Navbar />

      <div className="flex-1 max-w-3xl mx-auto px-4 pt-24 pb-16 w-full">
        {/* ── HEADER ── */}
        <div className="flex items-start justify-between gap-4 mb-10">
          <div>
            <p className="text-[10px] tracking-widest text-green-400/70 uppercase">
              Mi cuenta
            </p>
            <h1 className="text-3xl md:text-4xl font-bold mt-1">
              Hola, {user.first_name} 👋
            </h1>
          </div>

          {/* LOGOUT */}
          <button
            onClick={handleLogout}
            className="flex-shrink-0 px-4 py-2 rounded-xl border border-red-500/30 text-red-400 text-xs font-medium tracking-widest uppercase hover:bg-red-500/10 transition mt-1"
          >
            Cerrar sesión
          </button>
        </div>

        {/* ── PROFILE CARD ── */}
        <div className="bg-[#031c14] border border-green-900/30 rounded-2xl p-6 mb-8">
          <p className="text-[10px] tracking-widest text-green-400/70 uppercase mb-4">
            Información personal
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow
              label="Nombre"
              value={`${user.first_name} ${user.last_name}`}
            />
            <InfoRow label="Email" value={user.email} />
          </div>
        </div>

        {/* ── TABS ── */}
        <div className="flex bg-[#031c14] border border-green-900/30 rounded-xl p-1 mb-6">
          <TabButton
            label="Mis Pedidos"
            active={activeTab === "orders"}
            onClick={() => setActiveTab("orders")}
          />
          <TabButton
            label="Mis Entradas"
            active={activeTab === "tickets"}
            onClick={() => setActiveTab("tickets")}
          />
        </div>

        {/* ── ORDERS TAB ── */}
        {activeTab === "orders" && (
          <TabContent
            loading={ordersLoading}
            items={orders}
            emptyText="No tienes pedidos todavía"
            emptyIcon="🎟"
            renderItem={(item) => <OrderCard key={item.id} order={item} />}
          />
        )}

        {/* ── TICKETS TAB ── */}
        {activeTab === "tickets" && (
          <TabContent
            loading={ticketsLoading}
            items={tickets}
            emptyText="No tienes entradas todavía"
            emptyIcon="🎫"
            renderItem={(item) => <TicketCard key={item.id} ticket={item} />}
          />
        )}
      </div>

      <Footer />
    </div>
  );
}

/* ── TAB BUTTON ── */

function TabButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-2 rounded-lg text-sm font-medium tracking-widest uppercase transition
        ${
          active ? "bg-green-500 text-black" : "text-gray-400 hover:text-white"
        }`}
    >
      {label}
    </button>
  );
}

/* ── TAB CONTENT ── */

function TabContent({ loading, items, emptyText, emptyIcon, renderItem }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div
          className="w-8 h-8 rounded-full border-4 border-green-900/30 border-t-green-500"
          style={{ animation: "spin 1s linear infinite" }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center gap-3 text-gray-500">
        <span className="text-3xl">{emptyIcon}</span>
        <p className="text-sm">{emptyText}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">{items.map((item) => renderItem(item))}</div>
  );
}

/* ── INFO ROW ── */

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-[10px] tracking-widest text-gray-500 uppercase mb-1">
        {label}
      </p>
      <p className="text-sm text-white">{value}</p>
    </div>
  );
}

/* ── ORDER CARD ── */

function OrderCard({ order }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (amount, currency) => {
    const symbols = { EUR: "€", USD: "$", GBP: "£" };
    return `${symbols[currency] || currency} ${parseFloat(amount).toFixed(2)}`;
  };

  const statusConfig = {
    paid: {
      label: "Pagado",
      color: "text-green-400",
      bg: "bg-green-500/10 border-green-500/20",
    },
    pending: {
      label: "Pendiente",
      color: "text-yellow-400",
      bg: "bg-yellow-500/10 border-yellow-500/20",
    },
    expired: {
      label: "Expirado",
      color: "text-yellow-400",
      bg: "bg-yellow-500/10 border-yellow-500/20",
    },
    failed: {
      label: "Fallido",
      color: "text-red-400",
      bg: "bg-red-500/10 border-red-500/20",
    },
    cancelled: {
      label: "Cancelado",
      color: "text-gray-400",
      bg: "bg-gray-500/10 border-gray-500/20",
    },
  };

  const status = statusConfig[order.status] || statusConfig.pending;

  return (
    <div className="flex gap-4 bg-[#021610] border border-green-900/30 rounded-xl overflow-hidden hover:border-green-500/30 transition">
      {/* EVENT POSTER */}
      <div className="w-20 flex-shrink-0">
        {order.event_poster ? (
          <img
            src={order.event_poster}
            alt={order.event_name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-[#031c14] flex items-center justify-center min-h-[80px]">
            <span className="text-2xl">🎧</span>
          </div>
        )}
      </div>

      {/* CONTENT */}
      <div className="flex-1 py-4 pr-4 flex flex-col justify-between gap-2">
        {/* TOP: NAME + STATUS */}
        <div className="flex items-start justify-between gap-3">
          <p className="font-semibold text-sm leading-snug">
            {order.event_name}
          </p>
          <span
            className={`text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full border flex-shrink-0 ${status.bg} ${status.color}`}
          >
            {status.label}
          </span>
        </div>

        {/* DATE */}
        <p className="text-xs text-gray-400">
          {formatDate(order.event_date_start)}
        </p>

        {/* BOTTOM: ORDER NUMBER + TOTAL */}
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-mono text-gray-500">
            {order.order_number}
          </p>
          <p className="text-sm font-bold text-green-400">
            {formatCurrency(order.total, order.currency)}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── TICKET CARD ── */

function TicketCard({ ticket }) {
  console.log("---", ticket);
  const { token } = useUserStore();
  const [downloading, setDownloading] = useState(false);

  const formatCurrency = (amount, currency) => {
    const symbols = { EUR: "€", USD: "$", GBP: "£" };
    return `${symbols[currency] || currency} ${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleDownload = async () => {
    if (!ticket.pdf_url) return;
    try {
      setDownloading(true);
      const res = await fetch(ticket.pdf_url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed: " + res.status);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${ticket.reference_number || "ticket"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Ticket download error:", err);
    } finally {
      setDownloading(false);
    }
  };

  const totalPaid =
    ticket.price != null
      ? parseFloat(ticket.price) + parseFloat(ticket.commission_amount)
      : null;

  return (
    <div className="bg-[#021610] border border-green-900/30 rounded-xl overflow-hidden hover:border-green-500/30 transition">
      {/* TICKET STUB TOP */}
      <div className="px-5 pt-5 pb-4">
        {/* TOP: TICKET TYPE + DOWNLOAD */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="text-[12px] tracking-widest text-green-400/60 uppercase mb-1">
              {ticket.event?.name}
            </p>
            <p className="text-[8px] tracking-widest text-green-400/60 uppercase mb-1">
              {formatDate(ticket.event?.date_start)}
            </p>
            <p className="font-semibold text-white">
              {ticket.ticket_type?.name || "—"}
            </p>
          </div>

          {/* DOWNLOAD PDF */}
          {ticket.pdf_url && (
            <button
              onClick={handleDownload}
              disabled={downloading}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[10px] tracking-widest uppercase transition flex-shrink-0
      ${
        downloading
          ? "border-green-900/40 text-green-900 cursor-not-allowed"
          : "border-green-500/40 text-green-400 hover:bg-green-500 hover:text-black"
      }`}
            >
              <DownloadIcon className="w-3 h-3" />
              {downloading ? "..." : "PDF"}
            </button>
          )}
          {ticket.pdf_url === null && ticket.status === "swapped" && (
            <div className="relative group flex items-center gap-1.5">
              {/* SWAPPED BADGE */}
              <span className="text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full border bg-orange-500/10 border-orange-500/20 text-orange-400">
                Swapped
              </span>

              {/* INFO ICON */}
              <div className="relative">
                <div className="w-4 h-4 rounded-full border border-gray-500/50 text-gray-500 flex items-center justify-center text-[10px] cursor-help">
                  i
                </div>

                {/* TOOLTIP */}
                <div
                  className="absolute right-0 top-6 w-56 bg-[#02160e] border border-green-900/40 rounded-xl px-3 py-2.5 shadow-xl z-50
        invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200"
                >
                  <p className="text-[10px] text-gray-400 leading-relaxed">
                    This ticket has been sold on Ticketswap. If you think this
                    is a mistake, please contact us.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* DOTTED DIVIDER */}
        <div className="border-t border-dashed border-green-900/50 my-3 relative">
          <div className="absolute -left-5 top-1/2 -translate-y-1/2 w-4 h-4 bg-[#021610] rounded-full border-r border-green-900/30" />
          <div className="absolute -right-5 top-1/2 -translate-y-1/2 w-4 h-4 bg-[#021610] rounded-full border-l border-green-900/30" />
        </div>

        {/* BOTTOM DETAILS GRID */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
          <DetailItem label="Pedido" value={ticket.order_number || "—"} mono />
          <DetailItem label="Referencia" value={ticket.reference || "—"} mono />
          <DetailItem
            label="Total"
            value={
              totalPaid != null
                ? formatCurrency(totalPaid, ticket.currency)
                : "—"
            }
          />
        </div>
      </div>
    </div>
  );
}

function DetailItem({ label, value, mono }) {
  return (
    <div>
      <p className="text-[10px] tracking-widest text-gray-500 uppercase mb-1">
        {label}
      </p>
      <p className={`text-xs text-white ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
}
